import axios from 'axios';
import { getOssToken } from '@/api/index'; // 获取OSS上传token的接口
import * as OSS from 'ali-oss';
import Vue from 'vue';
import dayjs from 'dayjs';


/**
 * 普通上传至OSS（适用于小文件）
 * @param {File} file 文件对象
 * @param {string} dir 文件保存目录（例如 'uploads/')
 * @returns {Promise<string>} 返回最终文件URL
 */
export async function uploadSmallFileToOSS(file, dir = '') {
  try {
    Vue.prototype.$globalLoading.show('File uploading...', true);

    // 1. 获取OSS上传配置
    const date = dayjs().format('YYYYMMDD');
    const { data: ossConfig } = await getOssToken({dir:dir+'/'+date});
    console.log('ossConfig',ossConfig);

    // 2. 确保dir以"tssd/"开头
    if (!dir.startsWith('yichuanghub/')) {
      dir = `yichuanghub/${dir}/${date}`;
    }

    // 3. 构建文件存储路径（如果文件名本身不包含目录，则添加时间戳防止重名）
    // const timestamp = Math.floor(Date.now() / 1000);
    const nameHash = await uuid();
    const ext = file.name.split('.').pop();
    // const fileName = file.name.includes('/') ? file.name : `${timestamp}-${file.name}`;
    const fileName = `${nameHash}.${ext}`;

    const key = dir.endsWith('/') ? dir : dir+'/'+fileName;


    // 4. 创建FormData对象
    const formData = new FormData();
    formData.append('key', key);
    formData.append('policy', ossConfig.policy);
    formData.append('OSSAccessKeyId', ossConfig.accessKeyId);
    formData.append('signature', ossConfig.signature);
    formData.append('success_action_status', '200'); // 确保返回200状态码
    formData.append('Content-Disposition', 'filename=' + encodeURIComponent(file.name)); // 确保返回200状态码
    formData.append('file', file);

    // 5. 执行上传
    const response = await axios.post(ossConfig.host, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'api-usage': 'oss'
      },
      timeout: 300000,
      onUploadProgress: (progressEvent) => {
        const percent = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        Vue.prototype.$globalLoading.updateProgress(percent);
      }
    }).finally(()=>{
      Vue.prototype.$globalLoading.hide();
    });

    console.log('response',response);

    // 6. 返回文件URL
    return `${ossConfig.oss_url}/${key}`;
  } catch (error) {
    console.error('上传失败:', error);

    // 错误处理
    if (error.response) {
      // 服务器响应了错误的状态码
      console.error('服务器响应:', error.response.status, error.response.data);
      // 如果是签名过期，可以尝试重新获取签名并重试
      if (error.response.status === 403) {
        console.log('检测到403错误，尝试刷新签名后重试');
        try{
          // 重新获取OSS token
          const { data: newOssConfig } = await getOssToken();

          // 再次尝试上传
          const retryResponse = await axios.post(newOssConfig.host, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'api-usage': 'oss'
            },
            timeout: 30000,
            onUploadProgress: (progressEvent) => {
              const percent = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              Vue.prototype.$globalLoading.updateProgress(percent);
            }
          });
          Vue.prototype.$globalLoading.hide();
          return `${newOssConfig.host}/${key}`;
        }catch(e){
          Vue.prototype.$globalLoading.hide();
          this.$layer.msg('Uploading failed, please try again later!')
          console.warn(e);
        }
      }
    } else if (error.request) {
      // 请求已发出但没有收到响应
      console.error('无响应:', error.request);
      this.$layer.msg('Uploading failed, please try again later!')
      Vue.prototype.$globalLoading.hide();
    } else {
      // 其他错误
      console.error('请求设置出错:', error.message);
      this.$layer.msg('Uploading failed, please try again later!')
      Vue.prototype.$globalLoading.hide();
    }

    throw error;
  }
}

/**
 * 分片上传至OSS（适用于大文件）
 * @param {File} file 文件对象
 * @param {string} dir 文件保存目录
 * @returns {Promise<string>} 返回最终文件URL
 */
export async function uploadLargeFileToOSS(file, dir = '') {
  const client = new OSS({
    region: 'oss-cn-hangzhou', // 替换为你的OSS区域
    accessKeyId: '', // 将在获取token时填充
    accessKeySecret: '', // 将在获取token时填充
    stsToken: '', // 将在获取token时填充
    bucket: 'your-bucket-name', // 替换为你的OSS Bucket名称
    secure: true,
  });

  try {
    // 获取临时上传token
    const { data } = await getOssToken();
    const { AccessKeyId, AccessKeySecret, SecurityToken, host } = data;

    // 更新client配置
    client.options.accessKeyId = AccessKeyId;
    client.options.accessKeySecret = AccessKeySecret;
    client.options.stsToken = SecurityToken;

    const key = `${dir}${file.name}`;
    const partSize = 5 * 1024 * 1024; // 每个分片大小（5MB）
    const chunks = Math.ceil(file.size / partSize);
    let uploadId = '';

    // Step 1: 初始化分片上传任务
    const initResult = await client.initMultipartUpload(key);
    uploadId = initResult.uploadId;

    // Step 2: 并行上传各个分片
    const partETags = [];
    const partPromises = [];

    for (let i = 0; i < chunks; i++) {
      const start = i * partSize;
      const end = Math.min(start + partSize, file.size);
      const chunk = file.slice(start, end);

      partPromises.push(
        new Promise(async (resolve, reject) => {
          try {
            const result = await client.uploadPart(key, uploadId, i + 1, chunk);
            partETags.push({ PartNumber: i + 1, ETag: result.etag });
            resolve();
          } catch (err) {
            reject(err);
          }
        })
      );
    }

    await Promise.all(partPromises);

    // Step 3: 完成分片上传
    await client.completeMultipartUpload(key, uploadId, partETags);

    // 返回上传后的URL
    return `${host}/${key}`;
  } catch (error) {
    console.error('上传失败:', error);
    throw error;
  }
}

/**
 * 自动判断上传方式（普通 or 分片）
 * @param {File} file 文件对象
 * @param {string} dir 文件保存目录
 * @param {number} [threshold=5] 判断阈值（单位：MB）
 * @returns {Promise<string>}
 */
export async function uploadFileToOSS(file, dir = '', threshold = 5) {
    //>暂时只保留小文件直传
    return await uploadSmallFileToOSS(file, dir);
  const fileSizeInMB = file.size / (1024 * 1024); // 文件大小转换为 MB
  if (fileSizeInMB < threshold) {
    return await uploadSmallFileToOSS(file, dir);
  } else {
    return await uploadLargeFileToOSS(file, dir);
  }
}


async function getFileHash(file) {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-1', buffer);

  // 将 ArrayBuffer 转换为十六进制字符串
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

  return hashHex;
}
async function uuid() {
	var s = [];
	var hexDigits = "0123456789abcdef";
	for (var i = 0; i < 36; i++) {
		s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
	}
	s[14] = "4"; // bits 12-15 of the time_hi_and_version field to 0010
	s[19] = hexDigits.substr((s[19] & 0x3) | 0x8, 1); // bits 6-7 of the clock_seq_hi_and_reserved to 01
	s[8] = s[13] = s[18] = s[23] = "-";

	var uuid = s.join("");
	return uuid;
}
